import { useRef } from "react";

import { offset } from "@floating-ui/dom";

import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { useCanvasSearchHighlight } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { useTrackCanvasSearch } from "src/flowGraph/canvasSearch/useTrackCanvasSearch";
import { useCanvasSearchTermParam } from "src/flowGraph/useCanvasSearchTermParam";

export const FLOW_GRAPH_SEARCH_WINDOW_ID = "flow-graph-search";

export type OpenSearchWindowParams = {
  searchTerm?: string;
  keepLeftPaneOpen?: boolean;
  isOmnibox?: boolean;
};

export const useFlowGraphSearchWindow = () => {
  "use memo";
  const { open: openWindow, close: closeWindow } = useFloatingWindowsActions();
  const { setSearchTerm, clearSearchTerm } = useCanvasSearchTermParam();
  const { setHighlightedMatch } = useCanvasSearchHighlight();
  const flowGraphSearchTargetRef = useRef<HTMLDivElement | null>(null);
  const { trackOpenOmnibox, trackOpenShortcut } = useTrackCanvasSearch();

  const openSearchWindow = ({
    searchTerm = "",
    keepLeftPaneOpen = false,
    isOmnibox = false,
  }: OpenSearchWindowParams = {}) => {
    const nodeEditor = document.querySelector("[data-loc=node-editor-pane]");
    const header = document.querySelector("[data-loc=layout-header]");

    flowGraphSearchTargetRef.current = (nodeEditor ?? header) as HTMLDivElement;

    setSearchTerm(searchTerm);

    if (isOmnibox) {
      trackOpenOmnibox(searchTerm, 0);
    } else {
      trackOpenShortcut(searchTerm, 0);
    }

    openWindow(
      {
        id: FLOW_GRAPH_SEARCH_WINDOW_ID,
        type: "flow-graph-search",
        target: flowGraphSearchTargetRef,
        positionOptions: {
          placement: nodeEditor ? "left-start" : "bottom-end",
          middleware: [offset({ alignmentAxis: 20, mainAxis: 20 })],
        },
      },
      {
        searchTerm,
        keepLeftPaneOpen,
        isOmnibox,
      },
    );
  };

  const closeSearchWindow = () => {
    closeWindow(FLOW_GRAPH_SEARCH_WINDOW_ID);
    clearSearchTerm();
    setHighlightedMatch(null);
  };

  return {
    openSearchWindow,
    closeSearchWindow,
  };
};
